import {
  ApolloError,
  DefaultContext,
  Observable,
  TypedDocumentNode,
  WatchQueryFetchPolicy,
  useApolloClient,
} from '@apollo/client'
import { useState } from 'react'
import {
  DataCollectionPaginatedFetchOptions,
  DataCollectionSource,
  DataCollectionSourceDefinition,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  NavigationFiltersState,
  PaginatedResponse,
  RecordType,
  SortingsDefinition,
  SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'

import { DEFAULT_PAGE_SIZE, MapVariablesParams, PaginationVars } from 'modules/core/hooks/useGraphqlDataSource'

type PaginationType = 'pages' | 'infinite-scroll'
type FetchPolicy = WatchQueryFetchPolicy
type Keyed<T> = { [key: string]: T }
type VariablesRecord = Readonly<Record<string, unknown>>
type SourceCursorMap = Readonly<Record<string, string | null | undefined>>
type SourceHasNextPageMap = Readonly<Record<string, boolean | undefined>>

type TypedSourceDocument =
  | TypedDocumentNode<unknown, never>
  | TypedDocumentNode<unknown, VariablesRecord>

type SourceDefinitionLike = Readonly<{
  id: string
  document: TypedSourceDocument
}>

type DataBySource<
  Sources extends ReadonlyArray<SourceDefinitionLike>,
> = Readonly<Partial<{
  [Source in Sources[number] as Source['id']]: Source['document'] extends TypedDocumentNode<
    infer TResult,
    infer _TVariables
  >
    ? TResult
    : never
}>>

interface SourcePaginationParams {
  sourceId: string
  paginationType: PaginationType
  pagination: {
    perPage: number
    currentPage?: number
    cursor?: string | null
  }
  sourceCursor: string | null
}

export interface GraphqlCollectionSubSourceDefinition<
  Filters extends FiltersDefinition,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  SourceId extends string = string,
  TDocument extends TypedSourceDocument = TypedSourceDocument,
> {
  id: SourceId
  document: TDocument
  fetchPolicy?: FetchPolicy
  queryContext?: DefaultContext
  mapVariables: ({
    filters,
    sortings,
    search,
    navigationFilters,
    pagination,
  }: MapVariablesParams<Filters> & {
    navigationFilters?: NavigationFiltersState<Navigation>
  }) => VariablesRecord
  mapPaginationVariables?: (params: SourcePaginationParams) => PaginationVars
}

export interface MultiSourcePaginationResult {
  total: number
  cursors?: SourceCursorMap
  hasNextPageBySource?: SourceHasNextPageMap
}

export interface GraphqlMultiDataCollectionSourceDefinition<
  R extends RecordType = RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Summaries extends SummariesDefinition = SummariesDefinition,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<R> = GroupingDefinition<R>,
  Sources extends ReadonlyArray<
    GraphqlCollectionSubSourceDefinition<Filters, Navigation>
  > = ReadonlyArray<GraphqlCollectionSubSourceDefinition<Filters, Navigation>>,
> extends Omit<
  DataCollectionSourceDefinition<
    R,
    Filters,
    Sortings,
    Summaries,
    ItemActionsDefinition<R>,
    Navigation,
    Grouping
  >,
  'dataAdapter'
> {
  paginationType?: PaginationType
  perPage?: number
  sources: Sources
  mapData: (params: { sourcesData: DataBySource<Sources> }) => Array<R>
  mapSummaries?: (params: { sourcesData: DataBySource<Sources> }) => R | undefined
  mapPagination: (params: { sourcesData: DataBySource<Sources> }) => MultiSourcePaginationResult
}

export interface GraphqlMultiDataCollectionSource<
  R extends RecordType,
  Filters extends FiltersDefinition,
  Sortings extends SortingsDefinition,
  Summaries extends SummariesDefinition,
  Navigation extends NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<R>,
> extends DataCollectionSource<
  R,
  Filters,
  Sortings,
  Summaries,
  ItemActionsDefinition<R>,
  Navigation,
  Grouping
> {
  mappedVariables: Readonly<Record<string, VariablesRecord | undefined>>
  sources: ReadonlyArray<{ id: string; document: TypedSourceDocument }>
}

const PAGINATION_KIND_OFFSET: PaginationVars['__paginationKind'] = 'offset'
const PAGINATION_KIND_CURSOR: PaginationVars['__paginationKind'] = 'cursor'

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const defaultMapPaginationVariables = ({
  paginationType,
  pagination,
  sourceId,
  sourceCursor,
}: SourcePaginationParams): PaginationVars => {
  void sourceId

  if (paginationType === 'pages') {
    return {
      perPage: pagination.perPage,
      __notExcludePaginationKeysFromKeyArgs: true,
      page: pagination.currentPage ?? 1,
      __paginationKind: PAGINATION_KIND_OFFSET,
    }
  }

  if (sourceCursor && sourceCursor !== '0') {
    return {
      perPage: pagination.perPage,
      __notExcludePaginationKeysFromKeyArgs: true,
      cursor: sourceCursor,
      after: sourceCursor,
      __paginationKind: PAGINATION_KIND_CURSOR,
      __paginationInfiniteScroll: true,
    }
  }

  return {
    perPage: pagination.perPage,
    __notExcludePaginationKeysFromKeyArgs: true,
    __paginationKind: PAGINATION_KIND_CURSOR,
    __paginationInfiniteScroll: true,
  }
}

const decodeCompositeCursor = (
  cursor: string | number | null | undefined,
  sourceIds: ReadonlyArray<string>
): Readonly<Record<string, string | null>> => {
  const emptyCursors = sourceIds.reduce<Record<string, string | null>>((acc, sourceId) => {
    return {
      ...acc,
      [sourceId]: null,
    }
  }, {})

  if (!cursor || cursor === '0') {
    return emptyCursors
  }

  if (typeof cursor !== 'string') {
    const fallbackCursor = String(cursor)

    return sourceIds.reduce<Record<string, string | null>>((acc, sourceId) => {
      return {
        ...acc,
        [sourceId]: fallbackCursor,
      }
    }, {})
  }

  try {
    const parsed = JSON.parse(cursor)

    if (!isRecord(parsed)) {
      return sourceIds.reduce<Record<string, string | null>>((acc, sourceId) => {
        return {
          ...acc,
          [sourceId]: cursor,
        }
      }, {})
    }

    return sourceIds.reduce<Record<string, string | null>>((acc, sourceId) => {
      const value = parsed[sourceId]

      if (typeof value === 'string' || value === null) {
        return {
          ...acc,
          [sourceId]: value,
        }
      }

      return {
        ...acc,
        [sourceId]: null,
      }
    }, {})
  } catch {
    return sourceIds.reduce<Record<string, string | null>>((acc, sourceId) => {
      return {
        ...acc,
        [sourceId]: cursor,
      }
    }, {})
  }
}

const encodeCompositeCursor = (cursors: SourceCursorMap | undefined): string | null => {
  if (!cursors) {
    return null
  }

  const hasSomeCursor = Object.values(cursors).some((value) => Boolean(value))

  if (!hasSomeCursor) {
    return null
  }

  return JSON.stringify(cursors)
}

const toApolloError = (error: unknown): ApolloError => {
  if (error instanceof ApolloError) {
    return error
  }

  if (error instanceof Error) {
    return new ApolloError({ errorMessage: error.message })
  }

  return new ApolloError({ errorMessage: 'Unexpected GraphQL error' })
}

export const useGraphqlMultiDataCollectionSource = <
  Row extends RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Summaries extends SummariesDefinition = SummariesDefinition,
  Navigation extends NavigationFiltersDefinition = NavigationFiltersDefinition,
  Grouping extends GroupingDefinition<Row> = GroupingDefinition<Row>,
  Sources extends ReadonlyArray<
    GraphqlCollectionSubSourceDefinition<Filters, Navigation>
  > = ReadonlyArray<GraphqlCollectionSubSourceDefinition<Filters, Navigation>>,
>(
  {
    sources,
    mapData,
    mapPagination,
    mapSummaries,
    paginationType = 'pages',
    perPage = DEFAULT_PAGE_SIZE,
    ...rest
  }: GraphqlMultiDataCollectionSourceDefinition<
    Row,
    Filters,
    Sortings,
    Summaries,
    Navigation,
    Grouping,
    Sources
  >,
  deps: ReadonlyArray<unknown> = []
): GraphqlMultiDataCollectionSource<
  Row,
  Filters,
  Sortings,
  Summaries,
  Navigation,
  Grouping
> => {
  if (sources.length === 0) {
    throw new Error('At least one source is required')
  }

  const sourceIds = sources.map((source) => source.id)

  const client = useApolloClient()
  const [mappedVariables, setMappedVariables] = useState<Readonly<Keyed<VariablesRecord | undefined>>>(
    sourceIds.reduce<Keyed<VariablesRecord | undefined>>((acc, sourceId) => {
      return {
        ...acc,
        [sourceId]: undefined,
      }
    }, {})
  )

  const source = useDataCollectionSource<
    Row,
    Filters,
    Sortings,
    Summaries,
    ItemActionsDefinition<Row>,
    Navigation,
    Grouping
  >(
    {
      ...rest,
      dataAdapter: {
        perPage,
        paginationType,
        fetchData: (options: DataCollectionPaginatedFetchOptions<Filters, Navigation>) => {
          const { filters, sortings, search, navigationFilters } = options

          const pagination =
            'pagination' in options
              ? options.pagination
              : {
                  perPage: DEFAULT_PAGE_SIZE,
                  currentPage: 1,
                }

          const normalizedPagination = {
            perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
            currentPage: pagination?.currentPage ?? 1,
            cursor: pagination?.cursor ? String(pagination.cursor) : null,
          }

          const decodedCursor = decodeCompositeCursor(normalizedPagination.cursor, sourceIds)

          const sourceInputs = sources.map((source) => {
            const variables = source.mapVariables({
              filters,
              sortings,
              search,
              navigationFilters,
              pagination: normalizedPagination,
            })

            const paginationVars = (source.mapPaginationVariables ?? defaultMapPaginationVariables)(
              {
                sourceId: source.id,
                paginationType,
                pagination: normalizedPagination,
                sourceCursor: decodedCursor[source.id] ?? null,
              }
            )

            const fetchPolicy =
              paginationType === 'infinite-scroll' && decodedCursor[source.id]
                ? 'network-only'
                : (source.fetchPolicy ?? 'cache-and-network')

            return {
              source,
              variables,
              paginationVars,
              fetchPolicy,
            }
          })

          setMappedVariables(
            sourceInputs.reduce<Keyed<VariablesRecord | undefined>>((acc, item) => {
              return {
                ...acc,
                [item.source.id]: item.variables,
              }
            }, {})
          )

          return new Observable((observer) => {
            let active = true

            const run = async () => {
              observer.next({
                loading: true,
                error: undefined,
                data: undefined,
              })

              try {
                const results = await Promise.all(
                  sourceInputs.map(async ({ source, fetchPolicy, variables, paginationVars }) => {
                    const result = await client
                      .watchQuery({
                        query: source.document,
                        fetchPolicy,
                        variables: {
                          ...variables,
                          ...paginationVars,
                        },
                        context: source.queryContext,
                        notifyOnNetworkStatusChange: false,
                        nextFetchPolicy: 'cache-and-network',
                      })
                      .result()

                    return {
                      sourceId: source.id,
                      data: result.data,
                    }
                  })
                )

                if (!active) {
                  return
                }

                const sourcesData: DataBySource<Sources> = Object.assign(
                  {},
                  ...results.map((result) => ({
                    [result.sourceId]: result.data,
                  }))
                )

                const mappedData = mapData({
                  sourcesData,
                })

                const mappedPagination = mapPagination({
                  sourcesData,
                })

                const mappedSummaries = mapSummaries?.({
                  sourcesData,
                })

                const baseData = {
                  records: mappedData,
                  total: mappedPagination.total,
                  perPage: normalizedPagination.perPage,
                }

                const paginatedData: PaginatedResponse<Row> =
                  paginationType === 'pages'
                    ? {
                        ...baseData,
                        type: 'pages',
                        currentPage: normalizedPagination.currentPage,
                        pagesCount: Math.ceil(
                          mappedPagination.total / normalizedPagination.perPage
                        ),
                      }
                    : {
                        ...baseData,
                        type: 'infinite-scroll',
                        cursor: encodeCompositeCursor(mappedPagination.cursors) ?? null,
                        hasMore: Boolean(
                          mappedPagination.hasNextPageBySource &&
                            Object.values(mappedPagination.hasNextPageBySource).some(Boolean)
                        ),
                      }

                observer.next({
                  loading: false,
                  error: undefined,
                  data: {
                    ...paginatedData,
                    summaries: mappedSummaries,
                  },
                })
                observer.complete()
              } catch (error) {
                if (!active) {
                  return
                }

                observer.next({
                  loading: false,
                  error: toApolloError(error),
                  data: undefined,
                })
                observer.complete()
              }
            }

            void run()

            return () => {
              active = false
            }
          })
        },
      },
    },
    deps
  )

  return {
    ...source,
    mappedVariables,
    sources: sources.map((sourceItem) => ({
      id: sourceItem.id,
      document: sourceItem.document,
    })),
  }
}
